import { AgGridAngular } from "@ag-grid-community/angular";
import { ColDef, GridApi, GridOptions, ICellRendererParams, ValueGetterParams } from "@ag-grid-community/core";
import { ChangeDetectionStrategy, Component, inject, OnInit, signal } from "@angular/core";
import { toSignal } from "@angular/core/rxjs-interop";
import { Router } from "@angular/router";
import {
  AccountMarketplace,
  MatchType,
  ProductTrackerConfig,
  Strategy,
  StrategyStateEnum,
} from "@front/m19-api-client";
import { gridConfig } from "@front/m19-grid-config";
import { Catalog, SegmentConfigType, SegmentEx } from "@front/m19-models";
import {
  AccountSelectionService,
  AsinService,
  KeywordTrackingService,
  SegmentService,
  StrategyCache,
} from "@front/m19-services";
import {
  ActionButton,
  ActionButtonsComponent,
  ConfirmPopupComponent,
  IButtonDirective,
  IGridButtonComponent,
  IIconComponent,
  ITab,
  ITabsComponent,
  ModalService,
  ToasterService,
} from "@front/m19-ui";
import { TranslocoDirective, TranslocoService } from "@jsverse/transloco";
import { KeywordSegmentModalComponent } from "@m19-board/segments/keyword-segment-modal/keyword-segment-modal.component";
import { CsvExportService, fieldExtractor, simpleField } from "@m19-board/services/csv-export.service";
import { KeywordTrackerService } from "@m19-board/services/keyword-tracker.service";
import { LoadingService } from "@m19-board/services/loading.service";
import { InputModalComponent, InputModalData } from "@m19-board/shared/input-modal/input-modal.component";
import { ExportButtonComponent } from "@m19-board/shared/ui/export-buttons/export-button.component";
import { UntilDestroy, untilDestroyed } from "@ngneat/until-destroy";
import { combineLatest, forkJoin, of } from "rxjs";
import { switchMap, tap } from "rxjs/operators";
import { ProductTrackerPageQueryParams } from "../product-tracker/product-tracker-views/product-tracker.component";
import { ProductSegmentModalComponent, ProductSegmentModalData } from "./product-segment-modal.component";
import { SegmentDetailStrategiesModalComponent } from "./segment-detail/segment-detail-strategies-modal.component";

@UntilDestroy()
@Component({
  selector: "app-segment-manager",
  templateUrl: "./segment-manager.component.html",
  imports: [TranslocoDirective, ExportButtonComponent, AgGridAngular, ITabsComponent, IButtonDirective, IIconComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SegmentsComponent implements OnInit {
  private readonly accountSelection = inject(AccountSelectionService);
  private readonly segmentService = inject(SegmentService);
  private readonly asinService = inject(AsinService);
  private readonly keywordTrackingService = inject(KeywordTrackingService);
  private readonly modalService = inject(ModalService);
  private readonly router = inject(Router);
  private readonly toastrService = inject(ToasterService);
  private readonly keywordTrackerService = inject(KeywordTrackerService);
  private readonly csvExportService = inject(CsvExportService);
  private readonly translocoService = inject(TranslocoService);
  private readonly strategyCache = inject(StrategyCache);
  private readonly loadingService = inject(LoadingService);

  readonly tabs: ITab<SegmentConfigType>[] = [
    {
      value: SegmentConfigType.KeywordSegment,
      label: "Keyword Segments",
      url: "../keyword-segments",
    },
    {
      value: SegmentConfigType.ProductSegment,
      label: "Product Segments",
      url: "../product-segments",
      testId: "product-segment-btn",
    },
  ];
  readonly SegmentType = SegmentConfigType;

  segments = signal<SegmentEx[]>([]);
  accountMarketplace?: AccountMarketplace;
  strategiesBySegmentId: Map<number, Strategy[]> = new Map();
  isReadOnly = toSignal(this.accountSelection.readOnlyMode$, { requireSync: true });
  catalog?: Catalog;
  productTrackerConfig?: ProductTrackerConfig[];
  gridApi?: GridApi;
  selectedRows = signal<SegmentEx[]>([]);
  activeTab = signal<SegmentConfigType>(SegmentConfigType.KeywordSegment);

  gridOptions: GridOptions = {
    ...gridConfig({}),
    pagination: true,
    paginationPageSize: 20,
    sideBar: false,
    rowSelection: "multiple",
    suppressRowClickSelection: true,
    defaultColDef: {
      suppressHeaderMenuButton: true,
      resizable: false,
      suppressMovable: true,
    },
    onSelectionChanged: (event: any) => {
      this.selectedRows.set(event.api.getSelectedRows());
    },
    doesExternalFilterPass: (node: any) => {
      const segment: SegmentEx = node.data;
      return segment.segmentType == this.activeTab();
    },
    isExternalFilterPresent: () => true,
  };

  colDef: ColDef<SegmentEx>[] = [
    {
      field: "name",
      flex: 1.7,
      headerValueGetter: () => this.translocoService.translate("segment-manager.segment_name"),
      cellRendererSelector: (params: ICellRendererParams<SegmentEx>) => {
        return {
          component: IGridButtonComponent,
          params: {
            label: params.value,
            variant: "ghost",
            color: "gray",
            trailing: true,
            tooltipValue: this.translocoService.translate("segment-manager.change_segment_name"),
            icon: "edit",
            disabled: this.isReadOnly(),
            iconOnHover: true,
            clickAction: () => {
              this.editSegmentName(params.data!);
            },
          },
        };
      },
      checkboxSelection: true,
      headerCheckboxSelection: true,
      headerCheckboxSelectionFilteredOnly: true,
      floatingFilter: true,
      filter: "agTextColumnFilter",
      sort: "asc",
    },
    {
      colId: "strategies",
      flex: 1,
      cellStyle: { display: "flex", alignItems: "center" },
      headerValueGetter: (params) => this.translocoService.translate("common.strategies"),
      valueGetter: (params: any) => {
        return this.getStrategyCount(params.data);
      },
      comparator: (a: { active: number; paused: number }, b: { active: number; paused: number }) => {
        return a.active + a.paused - (b.active + b.paused);
      },
      valueFormatter: (params: any) => {
        const { active, paused } = params.value;
        const res = [];
        if (active > 0) {
          res.push(`${active} active`);
        }
        if (paused > 0) {
          res.push(`${paused} paused`);
        }
        return res.join(" | ");
      },
      cellRendererSelector: (params: any) => {
        const { active, paused } = params.value;
        const labels = [];
        let label = "-";
        if (active > 0) {
          labels.push(`${active} active`);
        }
        if (paused > 0) {
          labels.push(`${paused} paused`);
        }
        if (labels.length > 0) {
          label = labels.join(" | ");
        }
        return {
          component: IGridButtonComponent,
          params: {
            label,
            variant: "ghost",
            color: "gray",
            trailing: true,
            disabled: this.isReadOnly(),
            iconOnHover: true,
            clickAction: () => {
              this.modalService.openModal(SegmentDetailStrategiesModalComponent, {
                size: "lg",
                modalTitle: `${params.data.name} Strategies`,
                data: {
                  segment: params.data,
                  linkedStrategies: this.strategiesBySegmentId.get(params.data.segmentId) ?? [],
                },
              });
            },
          },
        };
      },
      floatingFilter: true,
      filter: "agNumberColumnFilter",
      // greater or equal default filter
      filterParams: {
        defaultOption: "greaterThanOrEqual",
      },
    },
    {
      colId: "products",
      flex: 1,
      headerValueGetter: (params) =>
        this.activeTab() === SegmentConfigType.KeywordSegment
          ? this.translocoService.translate("common.keywords")
          : this.translocoService.translate("common.products"),
      valueGetter: (params: ValueGetterParams<SegmentEx>) => {
        return params.data?.items.length;
      },
      valueFormatter: (params: any) => {
        if (this.activeTab() == SegmentConfigType.ProductSegment) {
          return params.value;
        }
        const segment: SegmentEx = params.data;
        return `${segment.items.filter((i) => i.matchType == MatchType.exact).length} exact | ${segment.items.filter((i) => i.matchType == MatchType.phrase).length} phrase`;
      },
      floatingFilter: true,
      filter: "agTextColumnFilter",
      filterValueGetter: (params: ValueGetterParams<SegmentEx>) => {
        return params.data?.items.map((item) => item.targetingValue).join(";");
      },
    },
    {
      flex: 0.2,
      minWidth: 125,
      // cell style to push the button to the right of the cell
      cellStyle: { display: "flex", justifyContent: "flex-end" },
      cellRendererSelector: (params: any) => {
        const segment: SegmentEx = params.data;
        const actionButtons: ActionButton[] = [];
        if (this.canSegmentBeDeleted(segment)) {
          actionButtons.push({
            title: "Remove",
            icon: "trash",
            tooltip: this.translocoService.translate("segment-manager.remove_segment_tt", [segment.name]),
            onClick: () => {
              this.deleteSegments([segment]);
            },
            disabled: this.isReadOnly() || !this.canSegmentBeDeleted(segment),
          });
        }
        actionButtons.push({
          title: "Edit",
          icon: "edit",
          tooltip: this.translocoService.translate("segment-manager.edit_segment_tt", [segment.name]),
          onClick: () => {
            this.editSegment(segment);
          },
          disabled: this.isReadOnly(),
        });
        const subItems: ActionButton[] = [];
        if (!this.isReadOnly()) {
          if (this.activeTab() == SegmentConfigType.KeywordSegment) {
            subItems.push({
              title: this.translocoService.translate("segment-manager.track_keywords"),
              onClick: () => {
                this.trackKeywords(segment);
              },
            });
          } else if (this.activeTab() == SegmentConfigType.ProductSegment) {
            subItems.push({
              title: this.translocoService.translate("segment-manager.track_products"),
              onClick: () => {
                this.trackProducts(segment);
              },
            });
          }
        }
        subItems.push({
          title: this.translocoService.translate("segment-manager.view_traffic_stats"),
          onClick: () => {
            this.viewTrafficAnalysis(segment);
          },
        });

        subItems.push({
          title: this.translocoService.translate("segment-manager.see_in_product_tracker"),
          onClick: () => {
            this.viewProductTracker(segment);
          },
        });
        actionButtons.push({
          icon: "ellipsisVertical",
          tooltip: this.translocoService.translate("segment-manager.more"),
          subItems: subItems,
        });
        return {
          component: ActionButtonsComponent,
          params: {
            actionButtons,
          },
        };
      },
    },
  ];

  ngOnInit(): void {
    this.loadingService.loadingOn();
    this.accountSelection.singleAccountMarketplaceSelection$
      .pipe(
        untilDestroyed(this),
        tap((am) => {
          this.accountMarketplace = am;
        }),
        switchMap((am) =>
          combineLatest([
            this.segmentService.getSegments(am.accountId, am.marketplace),
            this.strategyCache.getAllStrategyIndex(am.accountId, am.marketplace),
          ]),
        ),
      )
      .subscribe(([segments, strategies]) => {
        this.segments.set(Array.from(segments.values()));
        this.strategiesBySegmentId = new Map();
        for (const s of strategies.values()) {
          if (!s.isAdvertisingStrategy()) continue;

          for (const t of s.tactics) {
            if (!this.strategiesBySegmentId.has(t.segmentId)) {
              this.strategiesBySegmentId.set(t.segmentId, []);
            }
            this.strategiesBySegmentId.get(t.segmentId)!.push(s);
          }
        }
        this.loadingService.loadingOff();
      });
    this.accountSelection.singleAccountMarketplaceSelection$
      .pipe(
        untilDestroyed(this),
        switchMap((am) => this.asinService.getCatalog(am.accountId, am.marketplace)),
      )
      .subscribe((c) => {
        this.catalog = c;
      });
    this.accountSelection.singleAccountMarketplaceSelection$
      .pipe(
        untilDestroyed(this),
        switchMap((am) => this.keywordTrackingService.getProductTrackerConfig(am.accountId, am.marketplace)),
      )
      .subscribe((c) => {
        this.productTrackerConfig = c;
      });

    if (this.router.url.startsWith("/keyword-center/segments/keyword-segment")) {
      this.activeTab.set(SegmentConfigType.KeywordSegment);
    } else {
      this.activeTab.set(SegmentConfigType.ProductSegment);
    }
  }

  downloadCsv() {
    this.csvExportService.exportCsv(
      {
        prefix: `segments_list`,
        marketplace: this.accountMarketplace!.marketplace,
        accountGroupName: this.accountMarketplace!.accountGroupName,
      },
      this.segments(),
      [
        simpleField("segmentId"),
        simpleField("name"),
        fieldExtractor("strategies", (s) => {
          const { active, paused } = this.getStrategyCount(s);
          return `${active} active | ${paused} paused`;
        }),
        simpleField("segmentType"),
        fieldExtractor("products", (s) =>
          s.items
            .filter((i) => i.matchType == MatchType.asinSameAs)
            .map((i) => i.targetingValue)
            .join(";"),
        ),
        fieldExtractor("exactKeywords", (s) =>
          s.items
            .filter((i) => i.matchType == MatchType.exact)
            .map((i) => i.targetingValue)
            .join(";"),
        ),
        fieldExtractor("phraseKeywords", (s) =>
          s.items
            .filter((i) => i.matchType == MatchType.phrase)
            .map((i) => i.targetingValue)
            .join(";"),
        ),
      ],
    );
  }

  createNewSegment() {
    if (this.activeTab() == SegmentConfigType.ProductSegment) {
      this.modalService.openModal<ProductSegmentModalData, void>(ProductSegmentModalComponent, {
        modalTitle: `${this.translocoService.translate("common.create")} ${this.translocoService.translate("product-segment-modal.product_segment")}`,
        data: {
          accountId: this.accountMarketplace!.accountId,
          marketplace: this.accountMarketplace!.marketplace,
        },
      });
    } else if (this.activeTab() == SegmentConfigType.KeywordSegment) {
      this.modalService.openModal(KeywordSegmentModalComponent, {
        data: {
          accountId: this.accountMarketplace!.accountId,
          marketplace: this.accountMarketplace!.marketplace,
        },
        modalTitle: this.translocoService.translate("keyword-segment-modal.create_keyword_segment"),
      });
    }
  }

  onGridReady(event: any) {
    this.gridApi = event.api;
  }

  deleteSegments(segments: SegmentEx[]) {
    const ref = this.modalService.openModal(ConfirmPopupComponent, {
      modalTitle: this.translocoService.translate("segment-manager.confirm_segment_s_deletion"),
      data: {
        message: this.translocoService.translate(
          "segment-manager.are_you_sure_you_want_to_delete_the_selected_segments",
          { count: segments.length },
        ),
        confirmLabel: this.translocoService.translate("common.delete"),
      },
    });

    ref.afterClosed().subscribe((confirmed) => {
      if (confirmed) {
        const toDelete: number[] = [];
        for (const s of segments) {
          if (!this.canSegmentBeDeleted(s)) {
            this.toastrService.warning(
              this.translocoService.translate("segment-manager.segment_used_canot_delete", [s.name]),
              this.translocoService.translate("activity-service.segment_deletion"),
            );
            continue;
          }
          toDelete.push(s.segmentId);
        }
        if (toDelete.length > 0) {
          forkJoin(
            toDelete.map((segmentId) =>
              this.segmentService.deleteSegment(
                this.accountMarketplace!.accountId,
                this.accountMarketplace!.marketplace,
                segmentId,
              ),
            ),
          ).subscribe({
            next: () => {
              this.toastrService.success(
                this.translocoService.translate("segment-manager.segments_successfully_deleted"),
                this.translocoService.translate("segment-manager.segment_deleted"),
              );
            },
            error: (error: string) => {
              this.toastrService.error(
                this.translocoService.translate("segment-manager.error_deleting_segment_error", [error]),
                this.translocoService.translate("segment-manager.segment_deletion_error"),
              );
            },
          });
        }
      }
    });
  }

  private canSegmentBeDeleted(segment: SegmentEx) {
    // segment can be deleted when there are no strategy using it
    return (
      !this.strategiesBySegmentId.has(segment.segmentId) ||
      this.strategiesBySegmentId.get(segment.segmentId)!.length == 0
    );
  }

  private editSegment(segment: SegmentEx) {
    const segmentType = segment.segmentType;
    if (segmentType == SegmentConfigType.KeywordSegment) {
      this.modalService.openModal(KeywordSegmentModalComponent, {
        data: {
          segment: segment,
          isReadOnly: this.isReadOnly(),
          accountId: this.accountMarketplace!.accountId,
          initSegmentItems: segment.items,
          marketplace: this.accountMarketplace!.marketplace,
        },
        modalTitle: this.translocoService.translate("keyword-segment-modal.edit_keyword_segment"),
      });
    } else if (segmentType == SegmentConfigType.ProductSegment) {
      this.modalService.openModal<ProductSegmentModalData, void>(ProductSegmentModalComponent, {
        modalTitle: `${this.translocoService.translate("common.edit")} ${this.translocoService.translate("product-segment-modal.product_segment")}`,
        data: {
          accountId: this.accountMarketplace!.accountId,
          marketplace: this.accountMarketplace!.marketplace,
          segment: segment,
          isReadOnly: this.isReadOnly(),
        },
      });
    }
  }

  private getStrategyCount(segment: SegmentEx): { active: number; paused: number } {
    return (this.strategiesBySegmentId.get(segment.segmentId) ?? []).reduce(
      (acc, s) => {
        if (s.state == StrategyStateEnum.ENABLED) {
          acc.active++;
        } else {
          acc.paused++;
        }
        return acc;
      },
      { active: 0, paused: 0 },
    );
  }

  private viewProductTracker(segment: SegmentEx) {
    this.router.navigate(["/product-center/product-tracker"], {
      queryParams: { [ProductTrackerPageQueryParams.segment]: segment.segmentId },
    });
  }

  private viewTrafficAnalysis(segment: SegmentEx) {
    this.router.navigate(["keyword-center/traffic-analysis"], {
      queryParams: { selectedSegment: segment.segmentId },
    });
  }

  private editSegmentName(segment: SegmentEx) {
    const ref = this.modalService.openModal<InputModalData, string>(InputModalComponent, {
      modalTitle: this.translocoService.translate("segment-manager.change_segment_name"),
      data: {
        initialValue: segment.name,
        maxLength: 80,
      },
    });
    ref
      .afterClosed()
      .pipe(
        untilDestroyed(this),
        switchMap((newName: string) => {
          if (!newName || newName.trim() === "") {
            return of(undefined);
          }
          return this.segmentService.updateSegmentName(
            this.accountMarketplace!.accountId,
            this.accountMarketplace!.marketplace,
            segment.segmentId,
            newName,
          );
        }),
      )
      .subscribe((segment) => {
        if (segment === undefined) {
          this.toastrService.error(
            this.translocoService.translate("segment-manager.segment_name_cannot_be_empty"),
            this.translocoService.translate("common.error"),
          );
          return;
        }
        this.toastrService.success(this.translocoService.translate("segment-manager.segment_name_updated"));
      });
  }

  private trackProducts(segment: SegmentEx) {
    if (this.activeTab() != SegmentConfigType.ProductSegment) {
      return;
    }

    const ref = this.modalService.openModal(ConfirmPopupComponent, {
      modalTitle: "Track products",
      data: {
        message: "Are you sure you want to track these products?",
        confimLabel: `Track ${segment.items.length} product${segment.items.length > 1 ? "s" : ""}`,
      },
    });

    ref
      .afterClosed()
      .pipe(
        switchMap(() => {
          const newConfig: ProductTrackerConfig[] = [
            ...this.productTrackerConfig!,
            ...segment.items.map((i) => ({
              accountId: segment.accountId,
              marketplace: segment.marketplace,
              asin: i.targetingValue,
            })),
          ];
          return this.keywordTrackingService.addProductTrackerConfig(segment.accountId, segment.marketplace, newConfig);
        }),
      )
      .subscribe((config) => {
        this.productTrackerConfig = config;
        this.toastrService.success(
          this.translocoService.translate("segment-manager.started_tracking_n_new_product", [segment.items.length]),
          this.translocoService.translate("segment-manager.new_product_tracked"),
        );
      });
  }

  private trackKeywords(segment: SegmentEx) {
    if (this.activeTab() != SegmentConfigType.KeywordSegment) {
      return;
    }
    this.keywordTrackerService.openAddKwTrackingModal(
      segment.accountId,
      segment.marketplace,
      this.accountMarketplace!.resourceOrganizationId!,
      segment.items.map((i) => i.targetingValue),
    );
  }
}
